/**
 * Shared layout for every page under `/mail/*` (OCP-128).
 *
 * Renders a horizontal tabs strip + page-level page-container. Child
 * routes render via `<Outlet />` so each tab page only owns its own
 * body — header, page title, and the tabs strip stay consistent.
 *
 * OCP-212: tab visibility is segment-aware. Customers without an
 * explicit OMS subscription see only Overview + Sends (read-only) —
 * the rest of the surface (Domains, Templates, API keys, Webhooks,
 * Connections, Settings) is hidden because using them implies they
 * can mint API keys / configure delivery, which is reserved for
 * customers who buy an explicit OMS subscription. SaaS-tenant
 * included mail does not satisfy this gate (it's not a
 * portal_subscription row).
 */
import { useTranslation } from 'react-i18next';
import { NavLink, Outlet, useLocation } from 'react-router-dom';

import { useHasOmsSubscription } from '../lib/use-has-oms-subscription';
import { useMailComplaintsBadge } from '../lib/use-mail-complaints-badge';

/** `label` is a `mail-layout` catalogue key under `tabs.` (OCP-656). */
const ALL_TABS: Array<{ to: string; label: string; end?: boolean; gated?: boolean }> = [
  { to: '/mail', label: 'tabs.overview', end: true },
  { to: '/mail/sends', label: 'tabs.sends' },
  { to: '/mail/domains', label: 'tabs.domains', gated: true },
  { to: '/mail/templates', label: 'tabs.templates', gated: true },
  { to: '/mail/suppression', label: 'tabs.suppression', gated: true },
  { to: '/mail/api-keys', label: 'tabs.apiKeys', gated: true },
  { to: '/mail/webhooks', label: 'tabs.webhooks', gated: true },
  { to: '/mail/connections', label: 'tabs.connections', gated: true },
  { to: '/mail/settings', label: 'tabs.settings', gated: true },
];

export function MailLayout() {
  const { t } = useTranslation('mail-layout');
  const { loading, hasOms } = useHasOmsSubscription();
  const location = useLocation();
  // While loading, render all tabs to avoid a layout flicker — the
  // route-level MailFeatureGuard handles the actual gate on the
  // pages themselves.
  const tabs = loading || hasOms !== false
    ? ALL_TABS
    : ALL_TABS.filter((tab) => !tab.gated);
  const showOmsCta = !loading && hasOms === false;
  // OCP-621 - spam complaints this month, badged on the Sends tab.
  const badge = useMailComplaintsBadge(!loading && hasOms !== false);
  const showRedirectedBanner =
    new URLSearchParams(location.search).get('reason') === 'oms-sub-required';

  return (
    <div className="mx-auto max-w-6xl">
      <header className="mb-4">
        <h1 className="text-2xl font-semibold tracking-tight">{t('title')}</h1>
        <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
          {t('intro')}
        </p>
      </header>

      {showRedirectedBanner && (
        <div
          data-testid="oms-redirect-banner"
          className="mb-4 rounded border border-amber-200 dark:border-amber-900/50 bg-amber-50 dark:bg-amber-900/30 px-3 py-2 text-sm text-amber-900 dark:text-amber-200"
          role="status"
        >
          {t('redirected.text')}{' '}
          <a className="font-medium underline" href="/products?family=oms">
            {t('redirected.link')}
          </a>
        </div>
      )}

      {showOmsCta && (
        <div
          data-testid="oms-cta-banner"
          className="mb-4 rounded border border-brand-200 bg-brand-50 px-3 py-2 text-sm text-brand-900"
        >
          <strong>{t('cta.lead')}</strong> {t('cta.text')}{' '}
          <a className="font-medium underline" href="/products?family=oms">
            {t('cta.link')}
          </a>
          .
        </div>
      )}

      <nav
        aria-label={t('tabs.aria')}
        className="mb-6 flex flex-wrap gap-1 border-b border-gray-200 dark:border-gray-800"
      >
        {tabs.map((tab) => (
          <NavLink
            key={tab.to}
            to={tab.to}
            end={tab.end}
            className={({ isActive }) =>
              `-mb-px border-b-2 px-3 py-2 text-sm transition-colors ${
                isActive
                  ? 'border-brand-500 text-brand-700'
                  : 'border-transparent text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100'
              }`
            }
          >
            {t(tab.label)}
            {tab.to === '/mail/sends' && badge.complaints !== null && badge.complaints > 0 && (
              <span
                data-testid="mail-sends-complaints-badge"
                title={t('complaintsBadge', { count: badge.complaints })}
                className={`ml-1.5 inline-flex min-w-[1.25rem] justify-center rounded-full px-1.5 text-xs font-semibold ${badge.level === 'critical' ? 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300' : 'bg-amber-100 text-amber-900 dark:bg-amber-900/40 dark:text-amber-200'}`}
              >
                {badge.complaints}
              </span>
            )}
          </NavLink>
        ))}
      </nav>

      <Outlet />
    </div>
  );
}
